import { fetchBaseQuery, FetchBaseQueryError, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../index';
import {
  getExtendedToken,
  hasValidExtendedToken,
  getUserProfileWithAccessToken,
  refreshExtendedToken,
  resetAppAuthService,
} from '../../services/appAuthService';
import { performLogout } from '../../services/logoutService';
import { buildApiUrl } from '@/config/api.config';
import { STORAGE_KEYS } from '../../utils/storageKeys';

/**
 * Wait until the extended token becomes available in memory or localStorage.
 */
async function waitForExtendedToken(timeout = 5000, interval = 100): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const token = getExtendedToken() || localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) return token;
    await new Promise((resolve) => setTimeout(resolve, interval));
  }
  console.warn('⏳ Token was not available within timeout — proceeding without it');
  return null;
}

export const createExtendedTokenBaseQuery = (
  baseUrl: string
): BaseQueryFn<any, unknown, FetchBaseQueryError> => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: buildApiUrl(baseUrl),
    prepareHeaders: (headers, { getState }) => {
      const token =
        getExtendedToken() ||
        (getState() as RootState).auth.accessToken ||
        localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      } else {
        console.warn('⚠️ No token available for API call');
      }

      return headers;
    },
  });

  return async (args, api, extraOptions) => {
    // Step 1: Ensure we have a valid token before sending the request
    if (!hasValidExtendedToken()) {
      const stateToken =
        (api.getState() as RootState).auth.accessToken ||
        localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (stateToken) {
        // Try to exchange/refresh via the backend
        await getUserProfileWithAccessToken(stateToken);
      } else {
        // Wait for the auth flow to place a token
        const token = await waitForExtendedToken(5000);
        if (!token) {
          return {
            error: {
              status: 401,
              data: { message: 'No authentication token available' },
            } as FetchBaseQueryError,
          };
        }
      }
    }

    let result = await rawBaseQuery(args, api, extraOptions);

    // Step 2: On 401, attempt one token refresh then retry
    if (result.error && (result.error as FetchBaseQueryError).status === 401) {
      console.warn('🔄 Token invalid — trying to refresh…');

      try {
        const stateToken =
          (api.getState() as RootState).auth.accessToken ||
          localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

        if (stateToken) {
          const refreshed = await refreshExtendedToken(stateToken);
          if (refreshed?.access_token) {
            console.info('✅ Token refreshed — retrying request');
            result = await rawBaseQuery(args, api, extraOptions);
          } else {
            console.error('❌ Refresh failed — logging out');
            resetAppAuthService();
            performLogout();
          }
        } else {
          console.error('❌ No base token available — logging out');
          resetAppAuthService();
          performLogout();
        }
      } catch (err) {
        console.error('❌ Token refresh error:', err);
        resetAppAuthService();
        performLogout();
      }
    }

    return result;
  };
};
